import { ChangeDetectorRef, Component, HostListener, Injector, ViewChild, ViewEncapsulation } from '@angular/core';
import { ConfirmationService, TreeNode } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { DetailedWebtraderViewComponent } from '../../../shared/component/detailed-view/detailed-webtrader-view/detailed-webtrader-view.component';
import { TreeViewComponent } from '../../../shared/component/tree-view/tree-view.component';
import { AuthService } from '../../../shared/service/auth-service/auth-service.service';
import { Art57Section } from '../../../shared/ui/Art57Section/Art57Section';
import { ListView } from '../../../shared/ui/Data/ListView';
import { Art57Node } from '../../../shared/ui/Node/Art57Node/Art57Node';
import { Art57NodeUtils } from '../../../shared/ui/Node/Art57Node/Art57NodeUtils';
import { TreeNodeDrugIngredientsList } from '../../../shared/ui/Node/Art57Node/ListEntity/TreeNodeDrugIngredientsList';
import { BaseNode } from '../../../shared/ui/Node/BaseNode';
import { QueryNode } from '../../../shared/ui/Node/QueryNode';
import { SectionService } from '../../../shared/service/section-service/section-service';
import { QueryConditionsNode } from '../../../shared/ui/Node/QueryConditionsNode';
import { QueryNodeInBox } from '../../../shared/ui/Node/WebTraderNode/Query/QueryNodeInBox';
import { QueryNodeOutBox } from '../../../shared/ui/Node/WebTraderNode/Query/QueryNodeOutBox';
import { ErrorControlUtil } from '../../../shared/ui/ErrorControlUtil';
import { ErrorType } from '../../../shared/ui/Node/ErrorType';
import { FormlyFormBuilder } from '@ngx-formly/core';
import { ResultList } from '../../../shared/ui/Node/ListEntity/ResultList';
import { MINIMUM_TREE_WITDH } from '../../../shared/ui/Constants';
import { WebtraderService } from '../../../shared/service/webtrader-service/webtrader.service';
import { QueryNodeMessageBox } from '../../../shared/ui/Node/WebTraderNode/QueryNodeMessageBox';
import { WebTraderQueryResultNode } from '../../../shared/ui/Node/WebTraderNode/WebTraderQueryResultNode';
import { LoggerService } from '../../../shared/service/logger-service/logger.service';
import { BulkUpdateList } from '../../../shared/ui/Node/WebTraderNode/ListEntity/BulkUpdateList';
import { QueryNodeToEudravigilance } from '../../../shared/ui/Node/WebTraderNode/Query/QueryNodeToEudravigilance';
import { QueryNodeFromEudravigilance } from '../../../shared/ui/Node/WebTraderNode/Query/QueryNodeFromEudravigilance';


@Component({
  selector: 'app-webtrader',
  templateUrl: './webtrader.component.html',
  styleUrl: './webtrader.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class WebtraderComponent {
  @ViewChild('tree_view_component') tree_view_component!: TreeViewComponent;
  @ViewChild('detail_webtrader_view_component') detail_view_component!: DetailedWebtraderViewComponent;
  nodes: BaseNode[] = [];
  selectedNode: BaseNode | undefined;


  constructor(
    public authService: AuthService,
    private injector: Injector,
    public sectionService: SectionService,
    private cdr: ChangeDetectorRef,
    private formlyBuilder: FormlyFormBuilder,
    private confirmationService: ConfirmationService,
    private loggerService: LoggerService) {
  }

  //Initialize 
  ngOnInit() {
    this.initializeSection();
    this.sectionService.changeWebTraderSelectedNode$.subscribe({ next: node => this.onSelectedNodeChange(node) })
  }

  onResetApplication() {
    this.injector.get(Art57Section).resetApplication().then(applicationReseted => {
      if (applicationReseted) {
        this.initializeSection();
      }
    });
  }


  onResetSection() {
    this.injector.get(Art57Section).resetSection(this.injector.get(Art57Section).webtrader).then(sectionReseted => {
      if (sectionReseted) {
        this.initializeSection();
      }
    });
  }


  //Resizer event
  leftColumnWidth: number = this.injector.get(Art57Section).tree_width; // Initial width in pixels
  rightColumnWidth: number = window.innerWidth - this.leftColumnWidth;

  onResize(clientX: number): void {
    const container = document.querySelector('#main-container') as HTMLElement;

    if (container) {
      const containerRect = container.getBoundingClientRect();
      this.injector.get(Art57Section).tree_width = clientX - containerRect.left;

      const minWidth = MINIMUM_TREE_WITDH;
      const maxWidth = containerRect.width - 100;

      this.leftColumnWidth = Math.max(minWidth, Math.min(this.injector.get(Art57Section).tree_width, maxWidth));
    }
  }

  isResizing: boolean = false;
  onResizing(isResizing: boolean) {
    this.isResizing = isResizing;
  }


  onRefreshQuery(nodeToRefresh: BaseNode) {
    if (nodeToRefresh &&
      (nodeToRefresh instanceof QueryNodeInBox || nodeToRefresh instanceof QueryNodeOutBox 
        || nodeToRefresh instanceof QueryNodeToEudravigilance
        || nodeToRefresh instanceof QueryNodeFromEudravigilance)
    ) {
      nodeToRefresh.refreshQuery(nodeToRefresh);
    } else if (nodeToRefresh &&
      nodeToRefresh instanceof BulkUpdateList) {
        nodeToRefresh.getBulkUpdate();
    }
  }

  //Selection node event
  onNodeSelect(event: any) {
    this.selectedNode = event.node;
    this.injector.get(WebtraderService).loading.next(false);
    if (this.selectedNode) {
      this.injector.get(Art57Section).webtrader.selectedNode = this.selectedNode;
    }

    this.selectedNode!.children?.forEach((c: TreeNode) => (c as Art57Node).refreshLabel(false));
    if (this.selectedNode instanceof QueryNodeMessageBox) {
      this.onRefreshQuery(this.selectedNode!);
    }
  }

  onGoToSelectedNode(selectedNode: Art57Node) {
    this.onNodeSelect({ node: selectedNode });
    this.moveTreeViewToNode(this.selectedNode as TreeNode);
    this.expandParentNodes(selectedNode);
  }

  //Model changed
  onModelChange(model: any) {

    if (this.selectedNode) {

      if (this.selectedNode instanceof BaseNode) {
        this.selectedNode.refreshLabel();
      }

    }

  }

  initializeSection() {
    this.nodes = this.injector.get(Art57Section).webtrader.treeView;
    this.onNodeSelect({ node: this.injector.get(Art57Section).webtrader.selectedNode });
  }

  onSelectedNodeChange(selectedNode: any) {
    this.onNodeSelect({ node: selectedNode });
    this.expandParentNodes(selectedNode);
  }

  expandParentNodes(node: TreeNode) {
    node.expanded = true;
    if (node.parent) {
      this.expandParentNodes(node.parent); // Recursively expand children
    }
  }

  moveTreeViewToNode(node: TreeNode) {
    this.expandParentNodes(node);
    node!.expanded = true;
    this.cdr.detectChanges(); // allow detection that component view is draw in browser so we can change the focus in the right view
    this.setTreeViewScroll(node.key!);
  }


  setTreeViewScroll(key: string) {
    if (key != '') {

      const treeView = this.tree_view_component;
      if (treeView) {
        treeView.setScrollAtSelectedElement(key);
      }
    }
  }

  onRemoveNode(nodeToRemove: any) {
    let messageTxt = 'Are you sure you want to remove the selected Item?';
    if (nodeToRemove.parent instanceof ResultList) {
      messageTxt = 'Are you sure you want to remove the results from the list?';
    }
    this.confirmationService.confirm({
      header: 'Remove',
      message: messageTxt,
      icon: 'pi pi-question-circle',
      acceptLabel: 'OK',
      rejectLabel: 'Cancel',
      accept: () => {
        if (nodeToRemove && nodeToRemove.parent) {

          let parentNode: TreeNode = nodeToRemove.parent;
          parentNode!.children!.splice(parentNode!.children!.indexOf(nodeToRemove), 1);
          (parentNode as Art57Node).refreshLabel();

          this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Item has been removed', life: environment.successElapsedTime });

          if ((!(nodeToRemove.data instanceof ListView)) && (!(this.selectedNode instanceof TreeNodeDrugIngredientsList)) || (parentNode instanceof ResultList)) {
            this.onNodeSelect({ node: parentNode });
          }
        }
      },
      reject: () => {
        // do nothing
      }
    });
  }

  onRemoveSelectedNodes(nodeList: any) {

    if ((nodeList.children.filter((element: any) => element.checked)).length > 0) {
      this.confirmationService.confirm({

        header: 'Remove',
        message: 'Are you sure you want to remove all selected Items?',
        icon: 'pi pi-question-circle',
        acceptLabel: 'OK',
        rejectLabel: 'Cancel',
        accept: () => {
          if (nodeList) {
            nodeList.children = nodeList.children.filter((element: any) => !element.checked)
            this.selectedNode?.refreshLabel();
          }
          this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Items have been removed', life: environment.successElapsedTime });
        },
        reject: () => {
          // do nothing
        }
      });
    }
  }

  onButtonAction(buttonAction: any) {
    switch (buttonAction.action) {
      case 'resetApplication':
        this.onResetApplication();
        break;
      case 'resetSection':
        this.onResetSection();
        break;
      case 'remove':
        this.onRemoveNode(buttonAction.param);
        break;
      case 'removeall':
        this.onRemoveSelectedNodes(buttonAction.param);
        break;
      case 'loadMessage':
        this.onLoadMessage(buttonAction.param);
        break;
      case 'loadAck':
        this.onLoadAck(buttonAction.param);
        break;
      case 'runQuery':
        this.onRunQuery(buttonAction.param);
        break;
      case 'refreshQuery':
        this.onRefreshQuery(buttonAction.param);
        break;
      case 'moveToConditionsNode':
        this.onMoveToConditionsNode(buttonAction.param);
        break;
      case 'exportToExcel':
        this.exportToExcel(buttonAction.param);
        break;
      case 'runToExcel':
        this.runToExcel(buttonAction.param);
        break;
      case 'nextPage':
        this.onNextPage(buttonAction.param);
        break;
      case 'topPage':
        this.onTopPage(buttonAction.param);
        break;
      case 'previousPage':
        this.onPreviousPage(buttonAction.param);
        break;
      case 'validate':
        this.onSeeErrors(buttonAction.param);
        break;
    }

  }

  onMoveToConditionsNode(currentNode: any) {

    const conditionsNode = Art57NodeUtils.findFirstChildNodeOfType(currentNode.parent.parent, QueryConditionsNode);
    const newNode = (currentNode.conditionsNode as any).cloneNode(conditionsNode!.parent as BaseNode);
    currentNode.parent.parent.children[0] = newNode;
    const goToNode = Art57NodeUtils.findFirstChildNodeOfType(newNode.parent, QueryConditionsNode);

    this.onNodeSelect({ node: goToNode });
  }

  async onLoadMessage(jsonContent: string) {
    this.importXEVPRMessage(jsonContent);
  }

  async onLoadAck(jsonContent: string) {
    this.importXEVPRAck(jsonContent);
  }

  async importXEVPRAck(ackToImport: string) {

    let ack: any;
    try {
      ack = JSON.parse(ackToImport);
      const importedMessageNode = this.injector.get(Art57Section).webtrader.treeView.find(e => e.constructor.name == '_TreeNodeImportedMessagesList');
      let ichicsrmessageheader;
      ichicsrmessageheader = ack.ichicsrmessageheader;

      Art57NodeUtils.createAckxevmpd(ack, this.injector).then(ackxevmpd => {
        ackxevmpd.parent = importedMessageNode;
        this.injector.get(Art57Section).generateNumColumValue(ackxevmpd);
        ErrorControlUtil.toggleForms([ackxevmpd], this.formlyBuilder, true);
        importedMessageNode!.children?.push(ackxevmpd);
        importedMessageNode!.refreshLabel();
        this.onGoToSelectedNode(ackxevmpd);
        this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Data successfully imported', life: environment.successElapsedTime });
      });

    } catch (ex) {
      this.loggerService.err(ex);
      this.loggerService.info(ackToImport);

      this.injector.get(WebtraderService).loading.next(false);
      this.injector.get(Art57Section).showTooltipMessage({ severity: 'error', detail: 'Error importing ack', life: environment.errorElapsedTime });
    }

  }

  async importXEVPRMessage(messageToImport: string) {
    const start = performance.now();
    let message: any;
    try {
      message = JSON.parse(messageToImport);
      const importedMessageNode = this.injector.get(Art57Section).webtrader.treeView.find(e => e.constructor.name == '_TreeNodeImportedMessagesList');
      let ichicsrmessageheader;
      if (message.ichicsrmessageheader) {
        ichicsrmessageheader = message.ichicsrmessageheader;
      } else {
        ichicsrmessageheader = { messagetype: 'XEVPRM' };
      }
      Art57NodeUtils.createXEVPRM(message, this.injector).then(xevprmMessage => {

        xevprmMessage.parent = importedMessageNode;
        this.injector.get(Art57Section).generateNumColumValue(xevprmMessage);
        ErrorControlUtil.toggleForms([xevprmMessage], this.formlyBuilder, true);
        importedMessageNode!.children?.push(xevprmMessage);
        importedMessageNode!.refreshLabel();
        this.onGoToSelectedNode(xevprmMessage);
        this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Data successfully imported', life: environment.successElapsedTime });
      });
    } catch (ex) {
      this.loggerService.err(ex);
      this.loggerService.info(messageToImport);

      this.injector.get(WebtraderService).loading.next(false);
      this.injector.get(Art57Section).showTooltipMessage({ severity: 'error', detail: 'Error importing evprm message', life: environment.errorElapsedTime });
    } finally {
      const end = performance.now();
      const milliseconds = end - start;
      const seconds = milliseconds / 1000;
      this.loggerService.debug(`importXEVPRMessage took ${seconds} seconds to execute`)
    }

  }

  onSeeErrors(importMsg: any) {

    this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'Validating imported evprm message', life: 400 });

    setTimeout(() => {

      if (importMsg.fieldsErrorsControl.getErrors().length === 0) {
        let errorsFound = ErrorControlUtil.findErrors(this.injector, [importMsg], this.formlyBuilder, importMsg, true);
        errorsFound = errorsFound.filter((errorObject: any) => errorObject.errorType == ErrorType.lookupFormlyError);
        importMsg.fieldsErrorsControl.initializeErrors(errorsFound);
      }

      if (importMsg.fieldsErrorsControl.existsErrors() > 0) {
        this.injector.get(Art57Section).displayErrors(importMsg);
      } else {
        this.injector.get(Art57Section).showTooltipMessage({ severity: 'success', detail: 'All fields have been decoded, no errors found', life: environment.successElapsedTime });
      }

    }, 100);


  }

  onRunQuery(selectedNode: any) {

    let queryNode: any;
    // Scenario for Outbox or Inbox
    if ((selectedNode instanceof QueryNode)) {
      queryNode = selectedNode;
    } else {
      // Scenario for when the selected node is not a QueryNode, find the Outbox or Inbox
      queryNode = Art57NodeUtils.findFirstCurrentOrParentNodeOfType(selectedNode, QueryNodeMessageBox);
    }

    queryNode.executeQuery(selectedNode);
  }

  exportToExcel(selectedNode: WebTraderQueryResultNode) {
    selectedNode.exportToExcel();
  }

  runToExcel(selectedNode: BaseNode) {
    const queryNode: any = Art57NodeUtils.findFirstCurrentOrParentNodeOfType(selectedNode, QueryNode);
    queryNode.executeQueryToExcel();
  }

  onNextPage(selectedNode: any) {
    let queryNode = selectedNode;
    queryNode.nextPage(selectedNode);
    this.cdr.detectChanges();
  }

  onTopPage(selectedNode: any) {
    let queryNode = selectedNode;
    queryNode.topPage(selectedNode);
  }

  onPreviousPage(selectedNode: any) {
    let queryNode = selectedNode;
    queryNode.previousPage(selectedNode);
  }

  isTreeContainerVisible: boolean = true;

  toggleTreeContainer(): void {
    this.isTreeContainerVisible = !this.isTreeContainerVisible;
  }

}





